import {fetchSanApi, captureException, getUniqueNumber} from 'r/utils';
import {debounce} from 'lodash';

const groups = {};
let groupIdx = 0;
const MAX_RETRIES = 2;

const generateQueryFragment = (hash, {metric, slug, from, to, interval, source}) => `${hash}:getMetric(metric:"${metric}"){timeseriesData(slug:"${slug}",from:"${from.toISOString()}",to:"${to.toISOString()}",interval: "${interval}",selector: {source: "${source}"}){value datetime}}`;

const getAlternateSource = currentSource => currentSource === 'cryptocompare' ? 'coinmarketcap' : 'cryptocompare';

//Check for complexity error
const isComplexityError = response => response?.errors?.some(e =>
  e.message && e.message.includes('complexity'),
);

//Merging two API responses into one
const mergeResponses = (resp1, resp2) => {
  const merged = {data: {}, errors: []};

  //Glue the data together (timeseriesData)
  if (resp1?.data) {
    Object.assign(merged.data, resp1.data);
  }
  if (resp2?.data) {
    Object.assign(merged.data, resp2.data);
  }

  const filterErrors = errs => (errs || []).filter(e => !e.message?.includes('complexity'));
  merged.errors = [...filterErrors(resp1?.errors), ...filterErrors(resp2?.errors)];

  if (merged.errors.length === 0) {
    delete merged.errors;
  }

  return merged;
};

async function executeBatchRequest(batch) {
  const batchEntries = Object.entries(batch);

  if (batchEntries.length === 0) {
    return {data: {}};
  }

  const queryBody = `{${Object.values(batch).map(g => g.body).join('')}}`;

  try {
    const response = await fetchSanApi(queryBody).then(r => r.json());

    if (isComplexityError(response) && batchEntries.length > 1) {
      const mid = Math.ceil(batchEntries.length / 2);
      const batchA = Object.fromEntries(batchEntries.slice(0, mid));
      const batchB = Object.fromEntries(batchEntries.slice(mid));

      const [respA, respB] = await Promise.all([
        executeBatchRequest(batchA),
        executeBatchRequest(batchB),
      ]);

      return mergeResponses(respA, respB);
    }

    return response;
  }
  catch (e) {
    throw e;
  }
}


const debouncedGroupRequest = debounce(async () => {
  const idx = groupIdx;
  groupIdx++;

  const batch = groups[idx];
  if (!batch) {
    return;
  }

  const requeueGroup = group => {
    groups[groupIdx] ||= {};
    groups[groupIdx][group.hash] = group;
    debouncedGroupRequest();
  };

  try {
    const response = await executeBatchRequest(batch);

    const gqlErrors =
      response &&
      (response.errors || response.data && response.data.errors) ||
      [];

    const erroredAliases = new Set();
    const errorByAlias = new Map();

    if (Array.isArray(gqlErrors)) {
      gqlErrors.forEach(err => {
        const path = err?.path;
        const alias = Array.isArray(path) && path.length && path[0];
        if (typeof alias === 'string') {
          erroredAliases.add(alias);
          errorByAlias.set(alias, err);
        }
      });
    }

    Object.values(batch).forEach(group => {
      try {
        const node = response?.data?.[group.hash]?.timeseriesData;

        const hasExplicitError = erroredAliases.has(group.hash);
        const isEmptyArray = Array.isArray(node) && node.length === 0;
        const failed = hasExplicitError || !node || isEmptyArray;

        if (!failed) {
          group.resolve(node);
          return;
        }

        if (!group.__hasSwitchedSource) {
          const newSource = getAlternateSource(group.params.source);
          group.params.source = newSource;
          group.body = generateQueryFragment(group.hash, group.params);
          group.__hasSwitchedSource = true;
          group.__attempts = 0;
          requeueGroup(group);
          return;
        }

        group.__attempts ||= 0;
        if (group.__attempts < MAX_RETRIES) {
          group.__attempts += 1;
          requeueGroup(group);
        }
        else {
          const err = errorByAlias.get(group.hash) || new Error(`Failed to load ${group.hash}`);
          group.reject(err);
          captureException(err, {
            hash: group.hash,
            attempts: group.__attempts,
            switchedSource: group.__hasSwitchedSource,
          });
        }
      }
      catch (e) {
        group.__attempts ||= 0;
        if (group.__attempts < MAX_RETRIES) {
          group.__attempts += 1;
          requeueGroup(group);
        }
        else {
          group.reject(e);
          captureException(e, {phase: 'aliasProcess'});
        }
      }
    });
  }
  catch (e) {
    Object.values(batch).forEach(group => {
      group.__attempts ||= 0;
      if (group.__attempts < MAX_RETRIES) {
        group.__attempts += 1;
        groups[groupIdx] ||= {};
        groups[groupIdx][group.hash] = group;
      }
      else {
        group.reject(e);
      }
    });
    captureException(e, {phase: 'fetchGroup', idx});
    if (groups[groupIdx] && Object.keys(groups[groupIdx]).length) {
      debouncedGroupRequest();
    }
  }
  finally {
    delete groups[idx];
  }
}, 300);

let counter = 0;

export async function getPrices({slug, quote, from, to, interval, pairs}) {
  const token = pairs.find(t => t.quote === quote && t.slug === slug);
  if (!token) {
    const err = new Error('No slug + quote');
    captureException(err, {slug, quote});
    return Promise.reject(err);
  }

  const {metric, source} = token;
  const {promise, resolve, reject} = Promise.withResolvers();

  const hash = `${from}-${to}-${slug}-${quote}-${interval}-${getUniqueNumber()}`.replace(/((?![a-zA-Z0-9]).)*/g, '');

  const params = {metric, slug, from, to, interval, source};

  const group = {
    hash,
    resolve,
    reject,
    params,
    body: generateQueryFragment(hash, params),
    __attempts: 0,
    __hasSwitchedSource: false,
  };

  if (!groups[groupIdx]) {
    groups[groupIdx] = {[hash]: group};
  }
  else {
    groups[groupIdx][hash] = group;
  }

  counter++;
  debouncedGroupRequest();

  if (counter === 3) {
    debouncedGroupRequest.flush();
    counter = 0;
  }

  return promise;
}
